import { ICropStage } from '../../models/crop-stage/interfaces';
import { IFormFertilizer } from '../ranch-settings/modals/fertilizer/interfaces';
import { ITreeNutrientThreshold, IVegetativeNDemand } from '../../models/crop-type/interfaces';
import { FertilizationEvent } from './fertilizationEvent';
import { IEventPostResponseJSON } from '../../models/event/interfaces';
import { EventResponse } from '../../models/event/eventResponse';

export enum nUnits {
	LB_PER_ACRE = 1,
	FERTILIZER = 2
}

export interface IFertilizationFrequencyInDays {
	DaysToNextFertilization: number
}

export interface IFertilizationFrequencyPerMonth {

	FertilizationsPerMonth: number
}

export interface IVegetableFertilization {
	SoilN: number,
	SoilNppm: number,
	SoilNThreshold: number,
	SoilNThresholdppm: number,
	FractionalInterval: number,
	HarvestIntervalFraction: number
}

export interface FertilizationRecommendedSummaryViewModel {
	Recommendation: number,
	NUptakeDifference: number,
	NMineralTotal: number,
	NAppliedFromWater: number
}

export interface LettuceFertilizationRecommendedSummaryViewModel extends FertilizationRecommendedSummaryViewModel,
	IVegetableFertilization, IFertilizationFrequencyInDays {
}

export interface StrawberryFertilizationRecommendedSummaryViewModel extends FertilizationRecommendedSummaryViewModel,
	IVegetableFertilization, IFertilizationFrequencyPerMonth {

	NUptake: number,
	StrawberryFertilizationInterval: number,
	NUptakeThreshold: number,
	SoilNRatio: number,
	NFertilizerFactor: number,
	FertilizationsPerMonth: number,
}

export interface TomatoFertilizationRecommendedSummaryViewModel extends FertilizationRecommendedSummaryViewModel,
	IVegetableFertilization {

	NUptake: number,
	StrawberryFertilizationInterval: number,
	NUptakeThreshold: number,
	SoilNRatio: number,
	NFertilizerFactor: number,
	FertilizationsPerMonth: number,
}

export interface AlmondRecommendationSummaryModel extends FertilizationRecommendedSummaryViewModel,
	IFertilizationFrequencyInDays {

	MaxUptake: number,
	TotalCropNDemand: number,
	NSoilAmendments: number,
	UseEfficiency: number,
	ManureN: number,
	CompostN: number,
	CoverCropN: number
}

export interface ISoilSampleEmitted {
	Id: number,
	EventDate: Date,
	Response: IEventPostResponseJSON
}

export interface IRawFertilizationEvent {
	Metainfo: string,
	CropStage: ICropStage,
	SoilNitrate?: number,
	Id: number,
	FertilizerApplied?: number,
	FertilizerName: string,
	LbsPerGallon?: number,
	NApplied?: number,
	NPercentage?: number,
	NUptake: number,
	FormulationType: string,
	FormulationTypeId: number,
	SoilSampleEventDate?: string,
	NAppliedFromWater?: number,
	Recommendation: IFertilizationRecommendation,
	LastUpdatedUser: string,
	NutrientPercentages?: number[],
	IsChemical?: boolean
}

export interface IFertilizationEvent {
	FertilizerApplied: number,
	FertilizerName: string,
	FormulationType: string,
	FertilizerId?: number,
	Id: number,
	LastUpdatedUser: string,
	NApplied: number,
	NPercentage: number,
	IsFormulationTypeDry?: boolean,
	LbsPerGallon: number,
	Recommendation: IFertilizationRecommendation,
	managerNRecommendation: number, // redundant
	ManagerFertilizerAmount: number;
	SoilSampleEventDate: Date,
	Metainfo: string,
	DaysToNextFertilization?: number,
	FertilizationsPerMonth?: number
	EventDate: Date,
	endDate?: string,
	NAppliedFromWater: number,
	IrrigationMethodId?: number,
	IrrigationEventDate?: Date,
	IrrigationAmount?: number,
	UseWells?: boolean,
	PPMTotal?: number,
	IrrigationRecommendation?: number,
	ratio?: number,
	NutrientPercentages?: number[],
	IsChemical?: boolean,

	// raw model properties from combined events list
	CropStage: ICropStage,
	SoilNitrate: number,
	NUptake: number,
	FormulationTypeId: number,

	// View properties
	DisplayAmount?: number,
	DisplayFertilizationUnit?: number,
	DisplayLbsNPerAcre?: number,
	DateOnly?: boolean
}

export interface IFertilizationRecommendation {
	cropManage: number,
	manager?: number,
	managerFertilizerAmount?: number
}

export interface IFertilizationRecommendationSummaryParams {
	DaystoNextFertilization: number,
	FertilizationsPerMonth: number,
	SoilSampleEventDate: Date,
	EventDate: Date,
	NAppliedFromWater: number,
	EventId?: number,
}

/**
 * Used to parse JSON
 */
export interface FertilizationEventEditViewModel {
	CropStages: ICropStage[],
	CommodityTypeId: number, //
	Fertilizers: IFormFertilizer[], //
	SoilSamples: SoilSampleEventDropdownModel[], //
	startDate: string,
	endDate: string,
	NFertilizationRecommended?: number,
	NPercentage?: number,
	managerNRecommendation?: number,
	ManagerFertilizerAmount?: number,
	CropStageId?: number,
	DaysToNextFertilization: number,
	EventDate?: string, //
	FertilizerApplied?: number,
	FertilizerId?: number,
	IsChemical: boolean,
	IsPrivate: boolean,
	HarvestDate: string,
	NApplied?: number,
	SoilSampleEventDate?: string,
	IrrigationAmount?: number,
	IrrigationMethodId?: number,
	FertilizationsPerMonth?: number,
	IrrigationEventDate?: string,
	NAppliedFromWater?: number,
	UseWells: boolean,
	PPMTotal?: number,
	IrrigationRecommendation?: number,
	lastIrrigationMethod?: number,
	ratio?: number,
	YieldTarget?: number,
	LastUpdated?: string

	LbsPerGallon?: number, // virtual
	IsFormulationTypeDry?: boolean, // virtual
}

export interface IFertilizationEventGetRecommendationModelJSON {
	NFertilizationRecommended: number,
	Success: boolean,
	PreviousIrrigationEventDate: string,
	LastIrrigationMethodId: number,
	IrrigationUnitConversionRatio: number
}
export interface IFertilizationEventGetRecommendationModel {
	NFertilizationRecommended: number,
	Success: boolean,
	PreviousIrrigationEventDate: Date,
	LastIrrigationMethodId: number,
	IrrigationUnitConversionRatio: number
}

/**
 * model used to post to C#
 */
export interface IFertilizationEventGetSeasonalNUptakeModel {
	VegetativeNDemands: IVegetativeNDemand[],
	AgeOfCrop: number,
	IsHighVigor: boolean,
	TreeNutrientThresholds: ITreeNutrientThreshold[],
	YieldTarget?: number,
	NUptakeFactor: number,
	ExcessiveSummerLeafN?: number,
	InadequateSummerLeafN?: number,
	NFertilizerAdjustment?: number,
	CoverCropN: number,
	CompostN: number,
	ManureN: number,
	FertilizerUseEfficiency?: number,
	CommodityTypeId: number,
	Treecoefficients: TreeCoefficients
}

export interface TreeCoefficients {
	Intercept: number,
	LeafN: number,
	Ilr4: number,
	Ilr8: number,
	IlrProduct: number
}

/**
 * model returned from server
 */
export interface IFertilizationEventSeasonalNUptakeJSON {
	MaxUptake: number,
	TotalCropNDemand: number,
	YieldNDemand: number,
	VegetativeNDemand: number,
	LeafNAdjustment: number,
	PredictedSummerLeafN: number,
	CoverCropN: number,
	CompostN: number,
	ManureN: number,
	FertilizerUseEfficiency: number,
}

export interface IFertilizerProperties {
	Id: number,
	FormulationTypeName: string,
	IsFormulationTypeDry: boolean,
	LbsPerGallon: number,
	Name: string,
	NPercentage: number,
	OtherNutrients: string,
	IsChemical: boolean,
	IsPrivate: boolean,
	Success: boolean,
}

export interface SoilSampleEventDropdownModel {
	Id: number,
	Name: string,
	N: number,
	Date: string
}

export interface IPublicNitrogenUptakePoint {
	Date: Date,
	Amount: number,
}

export interface IPublicNitrogenRecommendationModel {
	CropTypeId: number,
	WetDate: Date,
	HarvestDate: Date,
	EventDate: Date,
	DaysToNextFertilization: number,
	FertilizationsPerMonth: number,
	AgeOfCrop: number,
	IsHighVigor: boolean,
	YieldTarget: number,
	LeafN: number,
	LeafK: number,
	LeafCa: number,
	LeafMg: number,
	LeafB: number,
	ManureN: number,
	CompostN: number,
	CoverCropN: number,
	SoilN: number,
	SampleDepth: number,
	Latitude: number,
	Longitude: number,
}

export interface IPublicNitrogenRecommendation {
	Recommendation: number,
	NUptake: number,
	NUptakeRequired: number,
	NUptakeDifference: number,
	IncludeSoilN: boolean,
	SoilN: number,
	SoilNThreshold: number,
	NMineralTotal: number,
	FertilizationsPerMonth?: number,
	DaysToNextFertilization: number,
	EventDate: Date,
	NextFertilizationDate: Date,
	NearHarvest: boolean,
	SeasonalN?: number,
	Uptake: IPublicNitrogenUptakePoint[],
}

export interface IFertilizationEventService {
	getCreateForm(plantingId: number): Promise<FertilizationEvent>,

	publicNitrogenRecommendation(model: IPublicNitrogenRecommendationModel):
		Promise<IPublicNitrogenRecommendation>,

	getRecommendationSummary(plantingId: number, fertilizationEvent: IFertilizationEvent): Promise<FertilizationRecommendedSummaryViewModel
		| AlmondRecommendationSummaryModel | StrawberryFertilizationRecommendedSummaryViewModel>,

	getSeasonalNUptake(params: IFertilizationEventGetSeasonalNUptakeModel):
		Promise<IFertilizationEventSeasonalNUptakeJSON>,

	getEditModel(id: number): Promise<FertilizationEvent>,
	create(plantingId: number, event: FertilizationEvent): Promise<IEventPostResponseJSON>,
	update(id: number, event: IFertilizationEvent, plantingId: number): Promise<EventResponse>,
	delete(id: number): Promise<IEventPostResponseJSON>,

	getNRecommendation(plantingId: number, event: FertilizationEvent):
		Promise<IFertilizationEventGetRecommendationModel>,
}
